/**
 * Product form with button-driven option pickers.
 *
 * Drives the HopHopShop PDP: color swatches and size chips are <button>s
 * (data-option-input). When the user picks one, the form re-resolves
 * the matching variant, updates the hidden id input, prices, stock
 * indicator, ATC button state, and notifies the gallery via cart:variant-changed.
 *
 * On submit, hijacks the form and posts to cart via the shared addToCart()
 * helper. If size hasn't been chosen, blocks submit and pulses the size grid
 * pink ("SELECT A SIZE").
 */
import { Component } from "@theme/component";
import { addToCart, announceCartStatus } from "@theme/cart-add";

class ProductForm extends Component {
  static ERROR_RESET_MS = 2000;
  static SHAKE_MS = 600;
  static PROMPT_MS = 2000;

  setup() {
    this.form = this.$("form");
    if (!this.form) return;

    // Attach submit hijack FIRST — guarantees no native /cart/add navigation
    // even if a later line in setup() throws.
    this.form.addEventListener("submit", (e) => this.#handleSubmit(e));

    this.submitButton = this.$('[type="submit"]');
    this.variantInput = this.$("[data-variant-id]");
    // [data-price] / [data-compare-price] live in the PDP info column,
    // OUTSIDE the <product-form> element, so a descendant query returns null.
    // The PDP renders one price row per page, so a document-scoped query is safe.
    this.priceEl = document.querySelector("[data-price]");
    this.compareEl = document.querySelector("[data-compare-price]");
    // The "Šifra" chip sits in the same column, for the same reason.
    this.skuEl = document.querySelector("[data-pdp-sku]");
    this.atcPriceEl = this.$("[data-atc-price]");
    this.atcLabelEl = this.$("[data-atc-label]");
    this.stockEl = this.$("[data-stock-indicator]");
    this.sizeGrid = this.$("[data-size-grid]");

    this.t = {
      adding: this.dataset.i18nAdding || "ADDING…",
      addToBag: this.dataset.i18nAddToBag || "ADD TO BAG",
      soldOut: this.dataset.i18nSoldOut || "SOLD OUT",
      outOfStock: this.dataset.i18nOutOfStock || "OUT OF STOCK",
      inStock: this.dataset.i18nInStock || "IN STOCK",
      onlyLeft: this.dataset.i18nOnlyLeft || "ONLY __N__ LEFT",
      error: this.dataset.i18nError || "ERROR — TRY AGAIN",
      unavailable: this.dataset.i18nUnavailable || "UNAVAILABLE",
      chooseSize: this.dataset.i18nChooseSize || "SELECT A SIZE",
    };

    const dataEl = this.$("[data-variant-data]");
    try {
      this.variants = JSON.parse(dataEl?.textContent || "[]");
    } catch {
      this.variants = [];
    }

    // Selected option values, indexed by position
    this.selected = new Map();
    this.$$("[data-option-input][aria-pressed='true']").forEach((btn) => {
      this.selected.set(
        Number(btn.dataset.optionPosition),
        btn.dataset.optionValue,
      );
    });
    // Make sure each position has *something* selected, for the price: the
    // first value still in stock, else the first button.
    this.$$("[data-option-input]:not([aria-disabled='true'])").forEach((btn) => {
      const pos = Number(btn.dataset.optionPosition);
      if (!this.selected.has(pos))
        this.selected.set(pos, btn.dataset.optionValue);
    });
    this.$$("[data-option-input]").forEach((btn) => {
      const pos = Number(btn.dataset.optionPosition);
      if (!this.selected.has(pos))
        this.selected.set(pos, btn.dataset.optionValue);
    });

    // Track whether the size grid has been touched (for the size-required UX)
    this.sizePosition = this.sizeGrid?.querySelector(
      "[data-option-input]",
    )?.dataset.optionPosition;
    this.sizePicked = false;
    // If a server-side selected size existed, count it as picked.
    if (
      this.sizeGrid &&
      this.sizeGrid.querySelector("[data-option-input][aria-pressed='true']")
    ) {
      this.sizePicked = true;
    }

    this.$$("[data-option-input]").forEach((btn) => {
      btn.addEventListener("click", (e) => this.#pickOption(btn, e));
    });

    this.#resolveVariant();
  }

  #pickOption(btn) {
    const pos = Number(btn.dataset.optionPosition);
    const val = btn.dataset.optionValue;

    // Mark this button active, deactivate siblings at the same position
    this.$$(`[data-option-input][data-option-position="${pos}"]`).forEach(
      (sibling) => {
        const active = sibling === btn;
        sibling.setAttribute("aria-pressed", String(active));
        sibling.classList.toggle("is-active", active);
      },
    );

    this.selected.set(pos, val);

    if (this.sizePosition && String(pos) === String(this.sizePosition)) {
      this.sizePicked = true;
      this.sizeGrid?.classList.remove("is-shake");
    }

    // Update label next to "COLOR: <value>" if present
    const valueEl = this.querySelector(`[data-option-value="color"]`);
    if (valueEl && this.colorPosition === pos) {
      valueEl.textContent = val;
    }

    this.#resolveVariant();
    document.dispatchEvent(
      new CustomEvent("product:variant-changed", {
        detail: {
          variant: this.currentVariant,
          selected: Object.fromEntries(this.selected),
        },
      }),
    );
  }

  #resolveVariant() {
    const wanted = [...this.selected.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, v]) => v);

    const match = this.variants.find((v) =>
      wanted.every((val, i) => v.options[i] === val),
    );

    this.currentVariant = match || null;

    if (match) {
      if (this.variantInput) this.variantInput.value = match.id;
      this.#renderPrice(match);
      this.#renderSku(match);
      this.#renderStock(match);
      this.#renderAtc(match);
    } else {
      this.#renderStock(null);
      this.#renderAtc(null);
    }
  }

  /**
   * The chip names the size in the cart, not the one the page opened with:
   * each size is its own article code ("32552-38"), and staff and buyers
   * quote it back to each other.
   */
  #renderSku(variant) {
    if (!this.skuEl) return;
    // Before a size is picked, the article code alone -- the first size's full
    // code would name a size nobody chose.
    const code = this.awaitingSize ? this.skuEl.dataset.articleCode : variant?.sku;
    if (!code) return;
    this.skuEl.textContent = code;
    this.skuEl.closest("[data-pdp-sku-chip]")?.removeAttribute("hidden");
  }

  #renderPrice(variant) {
    if (!this.priceEl) return;
    this.priceEl.textContent = this.#money(variant.price);

    const discountEl = document.querySelector("[data-discount-pct]");
    const savingsEl = document.querySelector("[data-savings]");
    const onSale =
      variant.compare_at_price && variant.compare_at_price > variant.price;

    if (onSale) {
      // Written as a var, not a literal, so the section's colour settings win.
      this.priceEl.style.color = "var(--pdp-price-sale, var(--color-pink-deep))";
      this.priceEl.classList.remove("text-ink");
      if (this.compareEl) {
        this.compareEl.textContent = this.#money(variant.compare_at_price);
        this.compareEl.hidden = false;
      }
      if (discountEl) {
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) /
            variant.compare_at_price) *
            100,
        );
        discountEl.textContent = `−${pct}%`;
        discountEl.hidden = false;
      }
      if (savingsEl) {
        const label = savingsEl.dataset.savingsLabel || "SAVE";
        savingsEl.textContent = `${label} ${this.#money(variant.compare_at_price - variant.price)}`;
        savingsEl.hidden = false;
      }
    } else {
      // Not "" — clearing would fall back to the classes, and text-pink-deep
      // from the initial sale render is never removed.
      this.priceEl.style.color = "var(--pdp-price, var(--color-ink))";
      this.priceEl.classList.add("text-ink");
      if (this.compareEl) {
        this.compareEl.textContent = "";
        this.compareEl.hidden = true;
      }
      if (discountEl) {
        discountEl.textContent = "";
        discountEl.hidden = true;
      }
      if (savingsEl) {
        savingsEl.textContent = "";
        savingsEl.hidden = true;
      }
    }
  }

  /** True while the shopper still has to pick a size (none is preselected). */
  get awaitingSize() {
    return !!this.sizeGrid && !this.sizePicked;
  }

  #renderStock(variant) {
    if (!this.stockEl) return;
    // Before a size is picked, speak for the product, not for one size.
    if (this.awaitingSize) variant = this.variants.find((v) => v.available) || variant;
    if (!variant) {
      this.stockEl.innerHTML = `<span class="hhs-pdp__stock-dot is-oos" aria-hidden="true"></span><span>${this.t.unavailable}</span>`;
      return;
    }
    if (variant.available) {
      const qty =
        typeof variant.inventory_quantity === "number"
          ? variant.inventory_quantity
          : null;
      if (qty !== null && qty > 0 && qty <= 2) {
        const text = this.t.onlyLeft.replace("__N__", String(qty));
        this.stockEl.innerHTML = `<span class="hhs-pdp__stock-dot is-low" aria-hidden="true"></span><span>${text}</span>`;
      } else {
        this.stockEl.innerHTML = `<span class="hhs-pdp__stock-dot is-in" aria-hidden="true"></span><span>${this.t.inStock}</span>`;
      }
    } else {
      this.stockEl.innerHTML = `<span class="hhs-pdp__stock-dot is-oos" aria-hidden="true"></span><span>${this.t.outOfStock}</span>`;
    }
  }

  #renderAtc(variant) {
    if (!this.submitButton) return;
    if (this.awaitingSize) {
      // Reads "add to bag" and stays clickable: a click without a size asks
      // for one instead (#promptSize).
      const any = this.variants.some((v) => v.available);
      this.submitButton.disabled = !any;
      if (this.atcLabelEl) this.atcLabelEl.textContent = any ? this.t.addToBag : this.t.soldOut;
      return;
    }
    const available = !!variant?.available;
    this.submitButton.disabled = !available;
    if (this.atcLabelEl) {
      if (available) {
        this.atcLabelEl.textContent = this.t.addToBag;
        this.atcPriceEl = null;
      } else {
        this.atcLabelEl.textContent = this.t.soldOut;
      }
    }
  }

  #money(cents) {
    const tpl = window.Shopify?.money_format || "${{amount}}";
    const centsNum = Number(cents);
    const amount = centsNum / 100;
    const locale = window.Shopify?.locale || "en";

    const fmt = (loc, decimals) =>
      new Intl.NumberFormat(loc, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

    // Locale-aware: respects the user's locale grouping/decimal conventions
    const grouped2 = fmt(locale, 2).format(amount);
    const grouped0 = fmt(locale, 0).format(Math.round(amount));

    // Forced European: "." groups, "," decimal — regardless of locale.
    // de-DE always uses this convention, so we borrow its formatter.
    const grouped2EU = fmt("de-DE", 2).format(amount);
    const grouped0EU = fmt("de-DE", 0).format(Math.round(amount));

    return tpl
      .replace("{{amount}}", grouped2)
      .replace("{{amount_no_decimals}}", grouped0)
      .replace("{{amount_with_comma_separator}}", grouped2EU)
      .replace("{{amount_no_decimals_with_comma_separator}}", grouped0EU);
  }

  async #handleSubmit(e) {
    e.preventDefault();

    // Size-required check (matches the design's pink-flash UX)
    if (this.sizeGrid && !this.sizePicked) {
      this.#promptSize();
      return;
    }
    if (!this.currentVariant?.available) return;

    const originalLabel = this.atcLabelEl
      ? this.atcLabelEl.innerHTML
      : this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute("aria-busy", "true");
    if (this.atcLabelEl) this.atcLabelEl.textContent = this.t.adding;

    try {
      await addToCart({ id: this.currentVariant.id, quantity: 1 });
      this.#restoreButton(originalLabel);
    } catch (error) {
      console.error("Add to cart error:", error);
      if (this.atcLabelEl) {
        // Show short reason from Shopify if available (e.g. "Sold out").
        const reason = (error?.message || "")
          .replace(/^Add to cart failed:\s*/i, "")
          .trim();
        this.atcLabelEl.textContent = reason
          ? reason.toUpperCase()
          : this.t.error;
      }
      announceCartStatus(
        error?.message || "Failed to add item to cart. Please try again.",
      );
      setTimeout(
        () => this.#restoreButton(originalLabel),
        ProductForm.ERROR_RESET_MS,
      );
    }
  }

  /**
   * Add to bag without a size: shake the size grid and let the button say
   * what is missing for a moment, then read "add to bag" again. Picking a
   * size meanwhile re-renders the label, so the timer only restores it while
   * a size is still awaited.
   */
  #promptSize() {
    this.sizeGrid.classList.add("is-shake");
    setTimeout(
      () => this.sizeGrid?.classList.remove("is-shake"),
      ProductForm.SHAKE_MS,
    );
    if (this.atcLabelEl) {
      this.atcLabelEl.textContent = this.t.chooseSize;
      clearTimeout(this.promptTimer);
      this.promptTimer = setTimeout(() => {
        if (this.awaitingSize) this.atcLabelEl.textContent = this.t.addToBag;
      }, ProductForm.PROMPT_MS);
    }
    document.dispatchEvent(new CustomEvent("product:size-required"));
  }

  #restoreButton(originalLabel) {
    this.submitButton.disabled = false;
    this.submitButton.removeAttribute("aria-busy");
    if (this.atcLabelEl) this.atcLabelEl.innerHTML = originalLabel;
  }
}

customElements.define("product-form", ProductForm);

export { ProductForm };
